import { doc } from '@atlaskit/adf-utils/doc';
import { paragraph as p } from '@atlaskit/adf-utils/paragraph';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { Inline } from '@atlaskit/adf-schema/inline-content';
import type {
	SelectionAdfResult,
	SelectionRange,
} from '@atlaskit/editor-plugin-selection-extension/types';

import { ACTION_ITEM_NODE_TYPES, TABLE_NODE_TYPES } from '../__types__/selectionAdfUtils.ts';

const isTextNode = (node: ADFEntity | null | undefined): node is ADFEntity & { text: string } =>
	!!node && node.type === 'text' && 'text' in node;

const INLINE_NODE_TYPES = new Set([
	'inlineCard',
	'mention',
	'emoji',
	'hardBreak',
	'status',
	'date',
]);

const isInlineNode = (node: ADFEntity): node is Inline =>
	isTextNode(node) || INLINE_NODE_TYPES.has(node.type);

const extractAllText = (node: ADFEntity | null | undefined): string => {
	if (!node) {
		return '';
	}
	if (isTextNode(node)) {
		return node.text;
	}
	if (Array.isArray((node as { content?: ADFEntity[] }).content)) {
		const childTexts = (node as { content: ADFEntity[] }).content.map(extractAllText);
		// Join child texts with a space and then normalise whitespace so we don't end up with merged words
		return childTexts.join(' ').replace(/\s+/g, ' ').trim();
	}
	return '';
};

/**
 * Gets the node by pointer
 * @param root - The root node
 * @param pointer - The pointer
 * @returns The node by pointer
 */
const getNodeByPointer = (root: ADFEntity, pointer: string): ADFEntity | null => {
	const segments = pointer.split('/').filter(Boolean);
	let current: ADFEntity | null = root;
	for (let i = 0; i < segments.length; i += 1) {
		const segment = segments[i];
		if (segment === 'content') {
			const index = Number(segments[i + 1]);
			i += 1;
			if (!current) {
				return null;
			}
			const content: ADFEntity[] | undefined = (current as { content?: ADFEntity[] }).content;
			if (!Array.isArray(content) || Number.isNaN(index)) {
				return null;
			}
			current = content[index] ?? null;
			continue;
		}
		if (segment === 'text') {
			return current;
		}
	}
	return current;
};

/**
 * Gets the text from the selection range
 * @param selectedNodeAdf - The selected node ADF
 * @param range - The selection range
 * @returns The text from the selection range
 */
const getTextFromSelectionRange = (
	selectedNodeAdf: ADFEntity,
	range: SelectionRange,
): string | null => {
	const { start, end } = range;
	if (!start?.pointer || !end?.pointer) {
		return null;
	}
	const targetNode = getNodeByPointer(selectedNodeAdf, start.pointer);
	if (!targetNode) {
		return null;
	}

	const nodeText = isTextNode(targetNode) ? targetNode.text : extractAllText(targetNode);
	if (start.position == null && end.position == null) {
		return nodeText;
	}
	const startPos = start.position ?? 0;
	const endPos = end.position ?? nodeText.length;
	if (startPos >= endPos) {
		return '';
	}
	return nodeText.slice(startPos, endPos);
};

/**
 * Extracts the text from the selection ranges
 * @param selectedNodeAdf - The selected node ADF
 * @param selectionRanges - The selection ranges
 * @returns The text from the selection ranges
 */
const extractTextFromSelectionRanges = (
	selectedNodeAdf: ADFEntity,
	selectionRanges: SelectionRange[],
): string | null => {
	let firstNonEmpty: string | null = null;
	let lastNonEmpty: string | null = null;
	const textSegments: string[] = [];

	for (const range of selectionRanges) {
		const rangeText = getTextFromSelectionRange(selectedNodeAdf, range);
		if (rangeText === null) {
			return null;
		}
		const trimmed = rangeText.trim();
		if (!trimmed) {
			continue;
		}
		if (firstNonEmpty === null) {
			firstNonEmpty = trimmed;
		}
		lastNonEmpty = trimmed;
		textSegments.push(rangeText);
	}

	const hasActionItems = containsActionItemType(selectedNodeAdf);
	const hasTable = containsTableType(selectedNodeAdf);

	if (hasActionItems && !hasTable) {
		// If only multiple action items are selected, use the first task's text as selection
		return firstNonEmpty ?? '';
	}

	if (!hasActionItems && !hasTable) {
		// For plain text selections combine all selected ranges
		if (textSegments.length === 0) {
			return '';
		}
		return textSegments.join('').trim();
	}

	// If multiple nodes selected (ex: table and action items), use the last piece of text
	return lastNonEmpty ?? '';
};

// This type is used to represent a text node with all formatting preserved (links, styles, etc.)
type TextNodeWithMarks = ADFEntity & { text: string; marks?: unknown[] };

/**
 * Clones a text node slice from the start position to the end position
 * @param node - The text node to clone
 * @param from - The start position
 * @param to - The end position
 * @returns The cloned text node slice
 */
const cloneTextNodeSlice = (
	node: TextNodeWithMarks,
	from: number,
	to: number,
): TextNodeWithMarks | null => {
	const slicedText = node.text.slice(from, to);
	if (!slicedText) {
		return null;
	}

	return {
		...node,
		text: slicedText,
	};
};

/**
 * Builds an ADF slice from the selection ranges
 * @param selectedNodeAdf - The selected node ADF
 * @param selectionRanges - The selection ranges
 * @returns The ADF slice
 */
const buildAdfSliceFromRanges = (
	selectedNodeAdf: ADFEntity,
	selectionRanges: SelectionRange[],
): ADFEntity | undefined => {
	const hasActionItems = containsActionItemType(selectedNodeAdf);
	const hasTable = containsTableType(selectedNodeAdf);
	const inlineSlices: ADFEntity[] = [];

	for (const range of selectionRanges) {
		const { start, end } = range;
		if (!start?.pointer || !end?.pointer) {
			continue;
		}

		const targetNode = getNodeByPointer(selectedNodeAdf, start.pointer);
		if (!isTextNode(targetNode)) {
			continue;
		}

		const fullTextLength = targetNode.text.length;
		const startPos = start.position ?? 0;
		const endPos = end.position ?? fullTextLength;
		if (startPos >= endPos) {
			continue;
		}

		const sliced = cloneTextNodeSlice(targetNode, startPos, endPos);
		if (!sliced) {
			continue;
		}

		inlineSlices.push(sliced);
	}

	if (inlineSlices.length === 0) {
		return undefined;
	}

	let content: ADFEntity[];

	if (hasActionItems && !hasTable) {
		content = [inlineSlices[0]];
	} else if (!hasActionItems && !hasTable) {
		content = inlineSlices;
	} else {
		content = [inlineSlices[inlineSlices.length - 1]];
	}

	return {
		type: 'paragraph',
		content: content as ADFEntity[],
	} as ADFEntity;
};

/**
 * Extracts the plain text from the selection ADF result
 * @param selectionAdfResult - The selection ADF result
 * @returns The plain text
 */
export const extractSelectedPlainText = (
	selectionAdfResult: SelectionAdfResult | null | undefined,
): string => {
	const selectedNodeAdf = selectionAdfResult?.selectedNodeAdf;
	if (!selectedNodeAdf) {
		return '';
	}
	const { selectionRanges } = selectionAdfResult ?? {};
	if (!selectionRanges || selectionRanges.length === 0) {
		const hasActionItems = containsActionItemType(selectedNodeAdf);
		const hasTable = containsTableType(selectedNodeAdf);

		// Case for multiple action items selection without a selectionRange, only take the first action item text
		if (hasActionItems && !hasTable) {
			const firstActionItemText = extractAllText(findFirstActionItemNode(selectedNodeAdf));
			if (firstActionItemText) {
				return firstActionItemText;
			}
		}

		return extractAllText(selectedNodeAdf);
	}
	return (
		extractTextFromSelectionRanges(selectedNodeAdf, selectionRanges) ??
		extractAllText(selectedNodeAdf)
	);
};

/**
 * Extracts the ADF slice from the selection ADF result
 * @param selectionAdfResult - The selection ADF result
 * @returns The ADF slice
 */
export const extractSelectedAdfSlice = (
	selectionAdfResult: SelectionAdfResult | null | undefined,
): ADFEntity | undefined => {
	const selectedNodeAdf = selectionAdfResult?.selectedNodeAdf;
	if (!selectedNodeAdf) {
		return undefined;
	}
	const { selectionRanges } = selectionAdfResult ?? {};
	if (!selectionRanges || selectionRanges.length === 0) {
		const hasActionItems = containsActionItemType(selectedNodeAdf);
		const hasTable = containsTableType(selectedNodeAdf);

		// Case for multiple action items selection without a selectionRange, only take the first action item text
		if (hasActionItems && !hasTable) {
			const firstActionItemText = extractAllText(findFirstActionItemNode(selectedNodeAdf));
			if (firstActionItemText) {
				return doc(p(firstActionItemText)) as unknown as ADFEntity;
			}
		}

		return selectedNodeAdf;
	}

	// Try to build a slice that preserves formatting from original ADF
	const adfSlice = buildAdfSliceFromRanges(selectedNodeAdf, selectionRanges);
	if (adfSlice) {
		return adfSlice;
	}

	// Fallback is to just use a plain-text slice when we can't construct a ADF slice from the ranges
	const selectionText = extractSelectedPlainText(selectionAdfResult);
	return selectionText ? (doc(p(selectionText)) as unknown as ADFEntity) : selectedNodeAdf;
};

export const normalizeToDocNode = (adf: ADFEntity | undefined): DocNode => {
	if (!adf) {
		return doc(p('')) as unknown as DocNode;
	}
	if (adf.type === 'doc') {
		return adf as DocNode;
	}
	if (isInlineNode(adf)) {
		return doc(p(adf)) as unknown as DocNode;
	}
	return {
		type: 'doc',
		version: 1,
		content: [adf],
	} as DocNode;
};

/**
 * Splits the text into a summary and a description
 * @param text - The text to split
 * @param maxLength - The maximum length of the summary
 * @returns The summary and the description
 */
const extractSummary = (text: string, maxLength: number): [string, string] => {
	const horizontalEllipsis = '…';
	if (text.length <= maxLength) {
		return [text, ''];
	}
	const firstPartLength = maxLength - horizontalEllipsis.length;
	const firstPart = text.substring(0, firstPartLength);
	const firstPartWithHorizontalEllipis = firstPart + horizontalEllipsis;
	return [firstPartWithHorizontalEllipis, text];
};

/**
 * Gets the summary and description from the selection ADF result
 * @param selectionAdfResult - The selection ADF result
 * @param maxLength - The maximum length of the summary
 * @returns The summary and the description
 */
export const getSummaryAndDescriptionFromSelectionAdfResult = (
	selectionAdfResult: SelectionAdfResult | null | undefined,
	maxLength: number,
): [string, DocNode] => {
	const selectionText = extractSelectedPlainText(selectionAdfResult);
	const selectionAdf =
		extractSelectedAdfSlice(selectionAdfResult) ?? selectionAdfResult?.selectedNodeAdf;
	const [summary] = extractSummary(selectionText, maxLength);
	const summaryWithoutFormatting = summary.replace(/[\r\n\t]+/g, ' ');
	return [summaryWithoutFormatting, normalizeToDocNode(selectionAdf)];
};

const TABLE_TYPES = new Set<string>(TABLE_NODE_TYPES);

/**
 * Checks if the node contains a table type
 * @param node - The node to check
 * @returns True if the node contains a table type, false otherwise
 */
const containsTableType = (node: ADFEntity | null | undefined): boolean => {
	if (!node) {
		return false;
	}
	if (TABLE_TYPES.has(node.type)) {
		return true;
	}
	const content = node.content;
	if (!content) {
		return false;
	}

	return content.some(containsTableType);
};

/**
 * Finds the first task item node within an ADF tree.
 * @param node - The root node to search from
 * @returns The first encountered taskItem node, or null if none is found
 */
const findFirstActionItemNode = (node: ADFEntity | null | undefined): ADFEntity | null => {
	if (!node) {
		return null;
	}

	const nodesToVisit: ADFEntity[] = [node];

	for (let i = 0; i < nodesToVisit.length; i += 1) {
		const current = nodesToVisit[i];

		if (current.type === 'taskItem') {
			return current;
		}

		const content = (current as { content?: ADFEntity[] }).content;
		if (Array.isArray(content) && content.length > 0) {
			nodesToVisit.push(...content);
		}
	}

	return null;
};

/**
 * Checks if the selection ADF result is a table selection
 * @param selectionAdfResult - The selection ADF result
 * @returns True if the selection ADF result is a table selection, false otherwise
 */
export const isTableSelectionAdf = (
	selectionAdfResult: SelectionAdfResult | null | undefined,
): boolean =>
	selectionAdfResult?.selectedNodeAdf?.type === 'table' &&
	!!selectionAdfResult.selectionRanges &&
	selectionAdfResult.selectionRanges.length > 1;

const ACTION_ITEM_TYPES = new Set<string>(ACTION_ITEM_NODE_TYPES);

/**
 * Checks if the node contains an action item type
 * @param node - The node to check
 * @returns True if the node contains an action item type, false otherwise
 */
const containsActionItemType = (node: ADFEntity | null | undefined): boolean => {
	if (!node) {
		return false;
	}
	if (ACTION_ITEM_TYPES.has(node.type)) {
		return true;
	}
	const content = (node as { content?: ADFEntity[] }).content;
	return Array.isArray(content) && content.some(containsActionItemType);
};

/**
 * Checks if the selection ADF result is an action items selection
 * @param selectionAdfResult - The selection ADF result
 * @returns True if the selection ADF result is an action items selection, false otherwise
 */
export const isActionItemsSelectionAdf = (
	selectionAdfResult: SelectionAdfResult | null | undefined,
): boolean => {
	const selectedNodeAdf = selectionAdfResult?.selectedNodeAdf;
	if (!selectedNodeAdf) {
		return false;
	}
	return containsActionItemType(selectedNodeAdf);
};
